'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { createInitialPreviewCacheByModelId, createPendingOptions, createPreviewCache, ensurePreviewCacheViews, getDisplayedPreviewOptions, getPendingColorOptions, getPreviewOptionKey, getSrcByView, isSamePendingOption, type PendingPreviewOption, type PreviewCacheByModelId, type PreviewSurface } from '@/app/lcv/components/sections/spartan/products/customize/configurator/hooks/utils';
import type { SpartanCustomizeModel, SpartanCustomizeModelColor, SpartanCustomizePreviewOption, SpartanCustomizePreviewView } from '@/app/lcv/components/sections/spartan/products/customize/types';
import { getSpartanCustomizePreviewOptions } from '@/app/lcv/components/sections/spartan/products/customize/utils';
interface UseCustomizePreviewCacheParams {
  selectedModel: SpartanCustomizeModel | null;
  previewOptions: SpartanCustomizePreviewOption[];
  activeView: SpartanCustomizePreviewView;
}
export const useCustomizePreviewCache = ({
  selectedModel,
  previewOptions,
  activeView
}: UseCustomizePreviewCacheParams) => {
  const modelId = selectedModel?.documentId ?? '';
  const [cacheByModelId, setCacheByModelId] = useState<PreviewCacheByModelId>(() => createInitialPreviewCacheByModelId(modelId, previewOptions));
  const [pendingPreviewOptions, setPendingPreviewOptions] = useState<PendingPreviewOption[]>([]);
  const [pendingThumbnailOptions, setPendingThumbnailOptions] = useState<PendingPreviewOption[]>([]);
  useEffect(() => {
    if (!modelId) {
      setPendingPreviewOptions([]);
      setPendingThumbnailOptions([]);
      return;
    }
    setCacheByModelId(prev => {
      const currentCache = prev[modelId];
      const nextCache = ensurePreviewCacheViews(currentCache, previewOptions);
      if (nextCache === currentCache) return prev;
      return {
        ...prev,
        [modelId]: nextCache
      };
    });
    setPendingPreviewOptions(prev => prev.filter(item => item.modelId === modelId));
    setPendingThumbnailOptions(prev => prev.filter(item => item.modelId === modelId));
    // Reset pending only on grade change. Color changes must keep stale preview visible.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- previewOptions changes on color swaps
  }, [modelId]);
  const selectedSrcByView = useMemo(() => getSrcByView(previewOptions), [previewOptions]);
  const cache = cacheByModelId[modelId];
  const previewCache = cache?.preview;
  const thumbnailCache = cache?.thumbnail;
  const displayedPreviewOptions = useMemo(() => getDisplayedPreviewOptions(previewOptions, previewCache, selectedSrcByView), [previewCache, previewOptions, selectedSrcByView]);
  const displayedThumbnailPreviewOptions = useMemo(() => getDisplayedPreviewOptions(previewOptions, thumbnailCache, selectedSrcByView), [previewOptions, selectedSrcByView, thumbnailCache]);
  const activeOption = previewOptions.find(option => option.view === activeView) ?? null;
  const activeDisplayedOption = displayedPreviewOptions.find(option => option.view === activeView) ?? null;
  const activeOptionKey = activeOption ? getPreviewOptionKey(activeOption) : '';
  const isActiveReady = !!activeOption && !!previewCache?.readyKeys.has(activeOptionKey);
  const currentPendingPreviewOptions = pendingPreviewOptions.filter(item => item.modelId === modelId).map(item => item.option);
  const pendingThumbnailPreviewOptions = pendingThumbnailOptions.filter(item => item.modelId === modelId).map(item => item.option);
  useEffect(() => {
    if (!activeOption || !modelId) return;
    if (activeDisplayedOption?.src === activeOption.src || isActiveReady) {
      setPendingPreviewOptions(prev => prev.filter(item => !isSamePendingOption(item, modelId, activeOption)));
      return;
    }
    setPendingPreviewOptions(prev => {
      if (prev.some(item => isSamePendingOption(item, modelId, activeOption))) {
        return prev;
      }
      return prev.concat({
        modelId,
        option: activeOption
      });
    });
  }, [activeDisplayedOption?.src, activeOption, isActiveReady, modelId]);
  const handleImageSettled = useCallback((surface: PreviewSurface, option: SpartanCustomizePreviewOption, isReady: boolean) => {
    if (!modelId) return;
    const optionKey = getPreviewOptionKey(option);
    setCacheByModelId(prev => {
      const currentCache = prev[modelId] ?? createPreviewCache(previewOptions);
      const currentSurfaceCache = currentCache[surface];
      const shouldDisplay = selectedSrcByView[option.view] === option.src;
      if (!shouldDisplay) return prev;
      const readyKeys = new Set(currentSurfaceCache.readyKeys);
      const wasReady = readyKeys.has(optionKey);
      if (isReady) {
        readyKeys.add(optionKey);
      } else {
        readyKeys.delete(optionKey);
      }
      const displayedSrcByView = currentSurfaceCache.displayedSrcByView[option.view] !== option.src ? {
        ...currentSurfaceCache.displayedSrcByView,
        [option.view]: option.src
      } : currentSurfaceCache.displayedSrcByView;
      if (wasReady === isReady && displayedSrcByView === currentSurfaceCache.displayedSrcByView) {
        return prev;
      }
      return {
        ...prev,
        [modelId]: {
          ...currentCache,
          [surface]: {
            readyKeys,
            displayedSrcByView
          }
        }
      };
    });
    if (surface === 'preview') {
      setPendingPreviewOptions(prev => prev.filter(item => !isSamePendingOption(item, modelId, option)));
      return;
    }
    setPendingThumbnailOptions(prev => prev.filter(item => !isSamePendingOption(item, modelId, option)));
  }, [modelId, previewOptions, selectedSrcByView]);
  const handlePreviewImageReady = useCallback((option: SpartanCustomizePreviewOption) => {
    handleImageSettled('preview', option, true);
  }, [handleImageSettled]);
  const handlePreviewImageError = useCallback((option: SpartanCustomizePreviewOption) => {
    handleImageSettled('preview', option, false);
  }, [handleImageSettled]);
  const handleThumbnailPreviewImageReady = useCallback((option: SpartanCustomizePreviewOption) => {
    handleImageSettled('thumbnail', option, true);
  }, [handleImageSettled]);
  const handleThumbnailPreviewImageError = useCallback((option: SpartanCustomizePreviewOption) => {
    handleImageSettled('thumbnail', option, false);
  }, [handleImageSettled]);
  const prepareColorPreview = useCallback((color: SpartanCustomizeModelColor) => {
    if (!selectedModel || !modelId) return;
    const options = getSpartanCustomizePreviewOptions(selectedModel, color);
    setPendingPreviewOptions(prev => prev.filter(item => item.modelId !== modelId).concat(createPendingOptions(modelId, getPendingColorOptions(options, previewCache))));
    setPendingThumbnailOptions(prev => prev.filter(item => item.modelId !== modelId).concat(createPendingOptions(modelId, getPendingColorOptions(options, thumbnailCache))));
  }, [modelId, previewCache, selectedModel, thumbnailCache]);
  return {
    displayedPreviewOptions,
    displayedThumbnailPreviewOptions,
    pendingPreviewOptions: currentPendingPreviewOptions,
    pendingThumbnailPreviewOptions,
    handlePreviewImageReady,
    handlePreviewImageError,
    handleThumbnailPreviewImageReady,
    handleThumbnailPreviewImageError,
    prepareColorPreview
  };
};